import { HBox, VBox } from "@ui/box/Box"
import { Typo } from "@ui/text/Typo"
import { formatCurrencyWithNumberToSymbolWithNumber } from "@ui/utils/formatter"
import { getTransactionAmountPresentation, getTransactionParties } from "src/utils/transactionPresentation"
import { DS, DetailRow, EntityHeader, Section } from "../ds/Kit"
import { useIsPrivateAccount } from "../private/PrivateAccountContext"
import { AmountHero, MoneyFlow, StatusPill, TxIcon, formatDateTime } from "./TransactionDetailsLive"

interface InternalTransferDetailsProps {
	entries: [ICompanyTrxTransaction & { uid: string }, ICompanyTrxTransaction & { uid: string }]
}

const PartyCard = ({ children }: { children: string }) => (
	<Typo sx={{ fontSize: "14px", fontWeight: 600, color: DS.ink, padding: "12px 14px", borderRadius: "12px", background: DS.soft, border: `1px solid ${DS.line}`, overflowWrap: "anywhere" }}>{children}</Typo>
)

export const InternalTransferDetails = ({ entries }: InternalTransferDetailsProps) => {
	const expense = entries.find((entry) => entry.direction === "expense") ?? entries[0]
	const income = entries.find((entry) => entry.direction === "income") ?? entries[1]
	const privateView = useIsPrivateAccount()
	const parties = getTransactionParties(expense, privateView)
	const amount = formatCurrencyWithNumberToSymbolWithNumber(Math.abs(Number(expense.amount) || 0), expense.currency)
	const created = formatDateTime(expense.created_at) || expense.created_at

	return (
		<VBox gap='16px' sx={{ minWidth: 0 }}>
			<EntityHeader
				stacked
				leading={<TxIcon type='transfer' />}
				title='Internal transfer'
				meta={created}
				badge={<StatusPill status={expense.status} />}
			/>

			<AmountHero label='Transfer amount' value={amount} />

			<Section title='Funds flow'>
				<MoneyFlow from={<PartyCard>{parties.from}</PartyCard>} to={<PartyCard>{parties.to}</PartyCard>} />
			</Section>

			<Section title='Transfer'>
				{expense.operation_id !== null && expense.operation_id !== undefined && (
					<DetailRow label='Operation ID' value={expense.operation_id} mono copy={String(expense.operation_id)} />
				)}
				{created && <DetailRow label='Date and time' value={created} />}
			</Section>

			<Section title='Ledger entries'>
				<Typo sx={{ fontSize: "13px", color: DS.muted, paddingBottom: "8px" }}>Two account entries belong to this single transfer.</Typo>
				{[expense, income].map((entry) => {
					const presentation = getTransactionAmountPresentation(entry)
					const formatted = formatCurrencyWithNumberToSymbolWithNumber(presentation.amount, entry.currency)
					const entryParties = getTransactionParties(entry, privateView)
					return (
						<HBox key={entry.uid} sx={{ justifyContent: "space-between", alignItems: "center", gap: "14px", padding: "12px 0", borderTop: `1px solid ${DS.line}` }}>
							<VBox gap='3px' sx={{ minWidth: 0 }}>
								<Typo sx={{ color: DS.ink, fontSize: "14px", fontWeight: 600 }}>{entry.direction === "expense" ? "Debit entry" : "Credit entry"}</Typo>
								<Typo sx={{ color: DS.muted, fontSize: "12px", overflowWrap: "anywhere" }}>{entryParties.from} → {entryParties.to}</Typo>
							</VBox>
							<Typo sx={{ color: presentation.color, fontSize: "14px", fontWeight: 700, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{presentation.sign}{formatted}</Typo>
						</HBox>
					)
				})}
			</Section>
		</VBox>
	)
}
